/**
 * Admin - Operations Team Management
 * Table-based layout with search, filters, and detailed information
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Avatar from "@mui/material/Avatar";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTable";

// API
import { adminAPI } from "utils/api";

function OperationsTeam() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // 0: Inspectors, 1: Check-In/Check-Out Agents
  const [inspectors, setInspectors] = useState([]);
  const [checkInOutAgents, setCheckInOutAgents] = useState([]);
  const [filteredInspectors, setFilteredInspectors] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [stats, setStats] = useState({
    inspectors: {
      total: 0,
      active: 0,
      assigned: 0,
      activeInspections: 0,
    },
    agents: {
      total: 0,
      active: 0,
    },
  });

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchOperationsTeam();
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [inspectors, checkInOutAgents, searchQuery, statusFilter, activeTab]);

  const fetchOperationsTeam = async () => {
    try {
      setLoading(true);

      // Fetch operations team (inspectors and check-in/check-out agents) and properties in parallel
      const [operationsTeamResponse, propertiesResponse] = await Promise.all([
        adminAPI.getOperationsTeam(),
        adminAPI.getAllProperties(),
      ]);

      const inspectorsList =
        operationsTeamResponse.inspectors?.members || operationsTeamResponse.inspectors || [];
      const agentsList =
        operationsTeamResponse.checkInOutAgents?.members ||
        operationsTeamResponse.checkInOutAgents ||
        [];
      const propertiesList = propertiesResponse.properties || propertiesResponse.data || [];

      // Process inspectors: count assignments and active inspections
      const processedInspectors = inspectorsList.map((inspector) => {
        const assignedProperties = propertiesList.filter(
          (p) =>
            p.assignedInspector &&
            (p.assignedInspector._id === inspector._id || p.assignedInspector === inspector._id)
        );
        const activeInspections = assignedProperties.filter(
          (p) => p.status === "Inspection"
        ).length;

        return {
          ...inspector,
          assignedCount: assignedProperties.length,
          activeInspections,
          completedInspections: assignedProperties.filter(
            (p) => p.status === "Inspection_Completed"
          ).length,
        };
      });

      // Process check-in/check-out agents
      const processedAgents = agentsList.map((agent) => ({
        ...agent,
      }));

      setInspectors(processedInspectors);
      setCheckInOutAgents(processedAgents);

      // Calculate statistics
      const inspectorStats = {
        total: processedInspectors.length,
        active: processedInspectors.filter((i) => i.isActive !== false).length,
        assigned: processedInspectors.reduce((sum, i) => sum + i.assignedCount, 0),
        activeInspections: processedInspectors.reduce((sum, i) => sum + i.activeInspections, 0),
      };

      const agentStats = {
        total: processedAgents.length,
        active: processedAgents.filter((a) => a.isActive !== false).length,
      };

      setStats({
        inspectors: inspectorStats,
        agents: agentStats,
      });
    } catch (error) {
      console.error("Error fetching operations team:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const dataToFilter = activeTab === 0 ? inspectors : checkInOutAgents;
    let filtered = [...dataToFilter];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (member) =>
          (member.fullName && member.fullName.toLowerCase().includes(query)) ||
          (member.email && member.email.toLowerCase().includes(query)) ||
          (member.phone && member.phone.toLowerCase().includes(query))
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      const isActive = statusFilter === "active";
      filtered = filtered.filter((member) => (member.isActive !== false) === isActive);
    }

    if (activeTab === 0) {
      setFilteredInspectors(filtered);
    } else {
      setFilteredAgents(filtered);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery("");
    setStatusFilter("all");
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (isActive) => {
    return isActive !== false ? "success" : "error";
  };

  const getStatusLabel = (isActive) => {
    return isActive !== false ? "Active" : "Inactive";
  };

  // Memoize statistics card props to prevent re-renders on scroll
  const statsCardProps = useMemo(
    () => ({
      inspectors: {
        total: {
          count: stats.inspectors.total,
          percentage: {
            color: "success",
            amount: `${stats.inspectors.active}`,
            label: "active",
          },
        },
        activeInspections: {
          count: stats.inspectors.activeInspections,
          percentage: {
            color: "info",
            amount: `${stats.inspectors.assigned}`,
            label: "total assignments",
          },
        },
      },
      agents: {
        total: {
          count: stats.agents.total,
          percentage: {
            color: "success",
            amount: `${stats.agents.active}`,
            label: "active",
          },
        },
      },
      total: {
        count: stats.inspectors.total + stats.agents.total,
        percentage: {
          color: "success",
          amount: `${stats.inspectors.active + stats.agents.active}`,
          label: "active members",
        },
      },
    }),
    [stats]
  );

  // Table columns for Inspectors (memoized to prevent re-renders)
  const inspectorColumns = useMemo(
    () => [
      {
        Header: "Inspector",
        accessor: "fullName",
        width: "20%",
        Cell: ({ value, row }) => (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                bgcolor: "info.main",
                width: 32,
                height: 32,
                fontSize: "0.875rem",
                fontWeight: "bold",
              }}
            >
              {getInitials(value)}
            </Avatar>
            <MDTypography variant="button" fontWeight="medium">
              {value || "N/A"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        width: "20%",
        Cell: ({ value }) => (
          <MDTypography
            variant="body2"
            color="primary"
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            component="a"
            href={`mailto:${value}`}
            onClick={(e) => e.stopPropagation()}
          >
            {value || "N/A"}
          </MDTypography>
        ),
      },
      { Header: "Phone", accessor: "phone", width: "12%" },
      {
        Header: "Status",
        accessor: "isActive",
        width: "10%",
        Cell: ({ value }) => (
          <Chip
            label={getStatusLabel(value)}
            color={getStatusColor(value)}
            size="small"
            icon={
              <Icon sx={{ fontSize: "1rem !important" }}>
                {value !== false ? "check_circle" : "cancel"}
              </Icon>
            }
          />
        ),
      },
      {
        Header: "Active Inspections",
        accessor: "activeInspections",
        width: "12%",
        Cell: ({ value }) => (
          <MDTypography variant="body2" fontWeight="medium" color="info.main">
            {value || 0}
          </MDTypography>
        ),
      },
      {
        Header: "Completed",
        accessor: "completedInspections",
        width: "12%",
        Cell: ({ value }) => (
          <MDTypography variant="body2" fontWeight="medium" color="success.main">
            {value || 0}
          </MDTypography>
        ),
      },
      {
        Header: "Total Assignments",
        accessor: "assignedCount",
        width: "12%",
        Cell: ({ value }) => (
          <MDTypography variant="body2" fontWeight="medium">
            {value || 0}
          </MDTypography>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "2%",
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/admin/operations-team/inspector/${row.original._id || row.original.id}`)
            }
            sx={{ color: "text.secondary" }}
          >
            <Icon>visibility</Icon>
          </IconButton>
        ),
      },
    ],
    [navigate]
  );

  // Table columns for Check-In/Check-Out Agents (memoized to prevent re-renders)
  const agentColumns = useMemo(
    () => [
      {
        Header: "Agent",
        accessor: "fullName",
        width: "25%",
        Cell: ({ value, row }) => (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                bgcolor: "warning.main",
                width: 32,
                height: 32,
                fontSize: "0.875rem",
                fontWeight: "bold",
              }}
            >
              {getInitials(value)}
            </Avatar>
            <MDTypography variant="button" fontWeight="medium">
              {value || "N/A"}
            </MDTypography>
          </MDBox>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        width: "30%",
        Cell: ({ value }) => (
          <MDTypography
            variant="body2"
            color="primary"
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            component="a"
            href={`mailto:${value}`}
            onClick={(e) => e.stopPropagation()}
          >
            {value || "N/A"}
          </MDTypography>
        ),
      },
      { Header: "Phone", accessor: "phone", width: "20%" },
      {
        Header: "Status",
        accessor: "isActive",
        width: "15%",
        Cell: ({ value }) => (
          <Chip
            label={getStatusLabel(value)}
            color={getStatusColor(value)}
            size="small"
            icon={
              <Icon sx={{ fontSize: "1rem !important" }}>
                {value !== false ? "check_circle" : "cancel"}
              </Icon>
            }
          />
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "10%",
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/admin/operations-team/agent/${row.original._id || row.original.id}`)
            }
            sx={{ color: "text.secondary" }}
          >
            <Icon>visibility</Icon>
          </IconButton>
        ),
      },
    ],
    [navigate]
  );

  // Prepare table rows (memoized to prevent re-renders)
  const inspectorRows = useMemo(
    () =>
      filteredInspectors.map((inspector) => ({
        ...inspector,
        fullName: inspector.fullName || "N/A",
        email: inspector.email || "N/A",
        phone: inspector.phone || "N/A",
      })),
    [filteredInspectors]
  );

  const agentRows = useMemo(
    () =>
      filteredAgents.map((agent) => ({
        ...agent,
        fullName: agent.fullName || "N/A",
        email: agent.email || "N/A",
        phone: agent.phone || "N/A",
      })),
    [filteredAgents]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold" mb={2}>
            Operations Team Management
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" mb={3}>
            Manage inspectors and check-in/check-out agents
          </MDTypography>

          {/* Statistics Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="info"
                icon="engineering"
                title="Total Inspectors"
                count={statsCardProps.inspectors.total.count}
                percentage={statsCardProps.inspectors.total.percentage}
                sx={{ height: "100%", minHeight: "180px" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="success"
                icon="assignment"
                title="Active Inspections"
                count={statsCardProps.inspectors.activeInspections.count}
                percentage={statsCardProps.inspectors.activeInspections.percentage}
                sx={{ height: "100%", minHeight: "180px" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="warning"
                title="Check-In/Check-Out Agents"
                icon="person_check"
                count={statsCardProps.agents.total.count}
                percentage={statsCardProps.agents.total.percentage}
                sx={{ height: "100%", minHeight: "180px" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                color="primary"
                icon="groups"
                title="Total Team Members"
                count={statsCardProps.total.count}
                percentage={statsCardProps.total.percentage}
                sx={{ height: "100%", minHeight: "180px" }}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Tabs */}
        <Card>
          <MDBox sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="operations team tabs"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  minHeight: 72,
                },
              }}
            >
              <Tab
                icon={<Icon>engineering</Icon>}
                iconPosition="start"
                label={`Inspectors (${inspectors.length})`}
              />
              <Tab
                icon={<Icon>person_check</Icon>}
                iconPosition="start"
                label={`Check-In/Check-Out Agents (${checkInOutAgents.length})`}
              />
            </Tabs>
          </MDBox>

          <MDBox p={3}>
            {/* Filters Section */}
            <MDBox mb={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon>search</Icon>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minHeight: "40px" }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth sx={{ minHeight: "40px" }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                      sx={{ minHeight: "40px" }}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => setShowFilters(!showFilters)}
                    fullWidth
                    sx={{ minHeight: "40px" }}
                  >
                    <Icon sx={{ mr: 1 }}>filter_list</Icon>
                    Filters
                  </MDButton>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearFilters}
                    fullWidth
                    sx={{ minHeight: "40px" }}
                  >
                    <Icon sx={{ mr: 1 }}>clear</Icon>
                    Clear Filters
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>

            {/* Inspectors Tab Content */}
            {activeTab === 0 && (
              <MDBox>
                {inspectors.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>engineering</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Inspectors Found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      No inspectors have been added to the system yet.
                    </MDTypography>
                  </MDBox>
                ) : filteredInspectors.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>search_off</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Results Found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Try adjusting your filters to see more results.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: inspectorColumns, rows: inspectorRows }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25, 50] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            )}

            {/* Check-In/Check-Out Agents Tab Content */}
            {activeTab === 1 && (
              <MDBox>
                {checkInOutAgents.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>person_check</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Check-In/Check-Out Agents Found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      No check-in/check-out agents have been added to the system yet.
                    </MDTypography>
                  </MDBox>
                ) : filteredAgents.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>search_off</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Results Found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Try adjusting your filters to see more results.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: agentColumns, rows: agentRows }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25, 50] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OperationsTeam;
